import { useCallback, useContext } from 'react';
import { useCrossFlow, Journeys } from '@atlassiansox/cross-flow-support/cross-flow-api-internals';
import { Targets } from '@atlassiansox/cross-flow-support/cross-flow-base-types';

import type {
	CrossFlowInteractedUIActionSubjectIDs,
	CrossFlowViewedUIActionSubjectIDs,
} from '@atlassian/surface-analytics-specs';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { LOOM_CROSS_SELL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

export interface CrossFlowRequestAccessProps {
	sourceComponent: CrossFlowViewedUIActionSubjectIDs;
	sourceContext: CrossFlowInteractedUIActionSubjectIDs;
}

export const useLoomCrossFlowRequestAccessCallback = ({
	sourceComponent,
	sourceContext,
}: CrossFlowRequestAccessProps): (() => Promise<void>) => {
	const crossFlow = useCrossFlow();
	const experienceTracker = useContext(ExperienceTrackerContext);

	return useCallback(async () => {
		if (!crossFlow.isEnabled) {
			experienceTracker.fail({
				name: LOOM_CROSS_SELL_EXPERIENCE,
				error: new Error('Loom crossflow is not enabled'),
				attributes: {
					sourceComponent,
					adType: 'crossFlow',
				},
			});
			return;
		}

		experienceTracker.succeed({
			name: LOOM_CROSS_SELL_EXPERIENCE,
			attributes: {
				sourceComponent,
				adType: 'crossFlow',
			},
		});

		try {
			/**
			 * Additionally, if the user *does* go through cross-flow, they'll be navigated to Loom.com and
			 * not return to this code path.
			 */
			await crossFlow.apiV1.open({
				journey: Journeys.GET_STARTED,
				sourceComponent,
				sourceContext,
				targetProduct: Targets.LOOM,
				v1: true,
			});
		} catch (error: any) {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.CONTENT_TYPES,
			});
		}
	}, [crossFlow, sourceComponent, experienceTracker, sourceContext]);
};
